import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Box,
  Button,
  Container,
  TextField,
  Card,
  CardContent,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { connect } from "react-redux";
import { COUNTRY_PHONE_CODES } from "src/utils/country_codes";
import { add_customer_async } from "src/actions/customer.actions";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  accent: "#222222",
  accentDark: "#000000",
};

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: 600,
    margin: "0 auto",
    paddingTop: 24,
    paddingBottom: 48,
  },
  pageHeader: { marginBottom: 24 },
  pageTitle: { fontSize: 24, fontWeight: 700, color: TOKENS.textPrimary },
  pageSubtitle: { fontSize: 14, color: TOKENS.textSecondary, marginTop: 2 },
  section: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  sectionHeader: { padding: "20px 24px 0 24px" },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: TOKENS.textPrimary },
  sectionSubtitle: { fontSize: 13, color: TOKENS.textSecondary, marginTop: 2 },
  sectionContent: { padding: "16px 24px 24px 24px" },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  submitButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "12px 32px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
    "&:disabled": { backgroundColor: "#C7D2FE", color: "#FFFFFF" },
  },
}));

const CreateCustomer = ({ country_code, add_customer_async }) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const code = !country_code ? "UG" : country_code;
  const zipCode = COUNTRY_PHONE_CODES[code];

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Add Customer</title>
      </Helmet>
      <Container className={classes.root}>
        <Box className={classes.pageHeader}>
          <Typography className={classes.pageTitle}>Add Customer</Typography>
          <Typography className={classes.pageSubtitle}>
            Add a new customer to your records
          </Typography>
        </Box>

        <Card className={classes.section}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>
              Customer Details
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              Name, email, and phone number
            </Typography>
          </Box>
          <CardContent className={classes.sectionContent}>
            <Formik
              initialValues={{
                email: "",
                full_name: "",
                phone_number: `+${zipCode}`,
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string()
                  .email("Must be a valid email")
                  .max(255)
                  .required("Email is required"),
                full_name: Yup.string().max(255).required("Name is required"),
                phone_number: Yup.string()
                  .max(255)
                  .required("Phone number is required"),
              })}
              onSubmit={async (values) => {
                const obj = {
                  ...values,
                  phone_number: values.phone_number.replace(/\D/g, ""),
                };
                await add_customer_async(obj, navigate);
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(touched.full_name && errors.full_name)}
                        fullWidth
                        helperText={touched.full_name && errors.full_name}
                        label="Full Name"
                        name="full_name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        defaultValue={values.full_name}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(touched.email && errors.email)}
                        fullWidth
                        helperText={touched.email && errors.email}
                        label="Email Address"
                        name="email"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="email"
                        defaultValue={values.email}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        className={classes.textField}
                        error={Boolean(
                          touched.phone_number && errors.phone_number,
                        )}
                        fullWidth
                        helperText={touched.phone_number && errors.phone_number}
                        label="Phone Number"
                        name="phone_number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="text"
                        defaultValue={values.phone_number}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    style={{ marginTop: 20 }}
                  >
                    <Button
                      disabled={isSubmitting}
                      type="submit"
                      variant="contained"
                      className={classes.submitButton}
                      startIcon={<PersonAddIcon style={{ fontSize: 18 }} />}
                    >
                      Add Customer
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  country_code: state.location.country_code,
});

export default connect(mapStateToProps, { add_customer_async })(CreateCustomer);
